import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import 'antd/dist/antd.css';
import { type Locale } from 'antd/es/locale';
import en from 'antd/es/locale/en_US';
import fr from 'antd/es/locale/fr_FR';
import pl from 'antd/es/locale/pl_PL';
import ru from 'antd/es/locale/ru_RU';
import tr from 'antd/es/locale/tr_TR';
import { updateLocale } from 'config/dayjs.config';
import config from 'config/react-query.config';
import { usePageTracking } from 'hooks/usePageTracking';
import i18next from 'i18next';
import { AuthProvider } from 'providers';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import theme from './App.theme';

const locales = {
  pl: pl,
  en: en,
  ru: ru,
  fr: fr,
  tr: tr,
};

type SupportedLng = keyof typeof locales;

const resolveLng = (lng?: string): SupportedLng => {
  const code = (lng ?? 'en').slice(0, 2);
  return (code in locales ? code : 'en') as SupportedLng;
};

export function Providers(props: React.PropsWithChildren<unknown>) {
  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={config.get()}>
        <BrowserRouter>
          <AuthProvider>
            <RoutedProviders {...props} />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function RoutedProviders(props: React.PropsWithChildren<unknown>) {
  usePageTracking();

  const [currentLocale, setCurrentLocale] = useState<Locale>(
    () => locales[resolveLng(i18next.resolvedLanguage)],
  );

  useEffect(() => {
    const applyLocale = (lng?: string) => {
      const locale = resolveLng(lng);
      setCurrentLocale(locales[locale]);
      updateLocale(locale);
    };

    applyLocale(i18next.resolvedLanguage);

    i18next.on('languageChanged', applyLocale);
    return () => {
      i18next.off('languageChanged', applyLocale);
    };
  }, []);

  return (
    <ConfigProvider
      form={{ validateMessages: { required: '${label} is required!' } }}
      locale={currentLocale}
      tag={{
        variant: 'outlined',
        styles: {
          root: {
            marginInlineEnd: 8,
          },
        },
      }}
      theme={{
        zeroRuntime: true,
        components: {
          Layout: {
            headerBg: '#fff',
          },
          Segmented: {
            trackBg: theme.color.lightGray3,
          },
        },
        token: { colorBgLayout: '#fcfeff', borderRadius: theme.size.borderRadius },
      }}
    >
      {props.children}
    </ConfigProvider>
  );
}
